import { AlertTriangle, Loader2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAdminCleanup } from "@/hooks/useAdminCleanup";

export const TestUserCleanup = ({ onCleanupComplete }) => {
  const cleanup = useAdminCleanup(onCleanupComplete);
  const canDelete = cleanup.confirmation === "DELETE TEST USERS" && cleanup.preview?.test_user_count > 0;

  return (
    <section className="rounded-2xl border border-[#E5E1D8] bg-white p-5 shadow-[0_8px_30px_rgba(26,54,38,0.04)] sm:p-7" data-testid="admin-test-cleanup-section">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="max-w-2xl">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8C9B90]" data-testid="admin-cleanup-overline">Test account cleanup</p>
          <h2 className="mt-2 font-serif-display text-3xl font-semibold text-[#1A3626]" data-testid="admin-cleanup-title">Preview obvious test users before deleting</h2>
          <p className="mt-2 text-sm leading-relaxed text-[#4A5D4E]" data-testid="admin-cleanup-copy">
            This only targets accounts using the safe test domain <strong>@example.com</strong>. Their reflections and reminder settings will be removed with the account.
          </p>
        </div>
        <Button type="button" onClick={cleanup.loadPreview} disabled={cleanup.loadingPreview || cleanup.deleting} className="rounded-full bg-[#1A3626] px-5 text-white hover:bg-[#4A5D4E]" data-testid="admin-preview-test-users-button">
          {cleanup.loadingPreview ? <Loader2 className="animate-spin" size={16} /> : <AlertTriangle size={16} />}
          Preview test users
        </Button>
      </div>

      {cleanup.error && <p className="mt-5 rounded-xl bg-[#FFF4F1] p-4 text-sm text-[#A85F4B]" data-testid="admin-cleanup-error">{cleanup.error}</p>}

      {cleanup.preview && (
        <div className="mt-6 space-y-5" data-testid="admin-cleanup-preview-panel">
          <div className="grid gap-3 sm:grid-cols-3" data-testid="admin-cleanup-preview-stats">
            <div className="rounded-xl bg-[#F9F6F0] p-4" data-testid="admin-cleanup-users-count-card">
              <p className="text-xs uppercase tracking-[0.16em] text-[#8C9B90]">Users</p>
              <p className="font-serif-display text-3xl font-semibold text-[#1A3626]" data-testid="admin-cleanup-users-count">{cleanup.preview.test_user_count}</p>
            </div>
            <div className="rounded-xl bg-[#F9F6F0] p-4" data-testid="admin-cleanup-reflections-count-card">
              <p className="text-xs uppercase tracking-[0.16em] text-[#8C9B90]">Reflections</p>
              <p className="font-serif-display text-3xl font-semibold text-[#1A3626]" data-testid="admin-cleanup-reflections-count">{cleanup.preview.reflection_count}</p>
            </div>
            <div className="rounded-xl bg-[#F9F6F0] p-4" data-testid="admin-cleanup-reminders-count-card">
              <p className="text-xs uppercase tracking-[0.16em] text-[#8C9B90]">Reminder settings</p>
              <p className="font-serif-display text-3xl font-semibold text-[#1A3626]" data-testid="admin-cleanup-reminders-count">{cleanup.preview.reminder_settings_count}</p>
            </div>
          </div>

          <div className="max-h-72 overflow-auto rounded-xl border border-[#E5E1D8]" data-testid="admin-cleanup-user-list">
            {cleanup.preview.users.length ? cleanup.preview.users.map((user) => (
              <div key={user.id} className="flex flex-col gap-1 border-b border-[#E5E1D8] px-4 py-3 last:border-b-0 sm:flex-row sm:items-center sm:justify-between" data-testid={`admin-cleanup-user-${user.id}`}>
                <div>
                  <p className="font-semibold text-[#1A3626]" data-testid={`admin-cleanup-user-${user.id}-name`}>{user.name}</p>
                  <p className="text-sm text-[#4A5D4E]" data-testid={`admin-cleanup-user-${user.id}-email`}>{user.email}</p>
                </div>
                <p className="text-sm text-[#8C9B90]" data-testid={`admin-cleanup-user-${user.id}-details`}>{user.reflection_count} reflections · reminders {user.reminder_enabled ? "on" : "off"}</p>
              </div>
            )) : (
              <p className="p-4 text-sm text-[#4A5D4E]" data-testid="admin-cleanup-empty">No obvious test users found.</p>
            )}
          </div>

          <div className="rounded-xl border border-[#C87961]/40 bg-[#FFF8F3] p-4" data-testid="admin-cleanup-confirm-panel">
            <label htmlFor="cleanup-confirmation" className="text-sm font-semibold text-[#1A3626]" data-testid="admin-cleanup-confirm-label">
              Type DELETE TEST USERS to confirm
            </label>
            <div className="mt-3 flex flex-col gap-3 sm:flex-row">
              <input
                id="cleanup-confirmation"
                value={cleanup.confirmation}
                onChange={(event) => cleanup.setConfirmation(event.target.value)}
                className="h-11 flex-1 rounded-xl border border-[#E5E1D8] bg-white px-3 text-[#1A3626] outline-none focus:ring-2 focus:ring-[#C87961]"
                data-testid="admin-cleanup-confirm-input"
              />
              <Button type="button" onClick={cleanup.deleteTestUsers} disabled={!canDelete || cleanup.deleting} className="rounded-full bg-[#A85F4B] px-5 text-white hover:bg-[#8F4C3C]" data-testid="admin-delete-test-users-button">
                {cleanup.deleting ? <Loader2 className="animate-spin" size={16} /> : <Trash2 size={16} />}
                Delete test users
              </Button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};